import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { canAccessAppraisals } from '@/auth/visibility';
import { useModuleRegistries } from '@/shared/modules/store';
import type { ModuleDef, NavGroup } from '@/shared/modules/registries';
import { isNavItemVisible } from '@/shell/navVisibility';
import { useQuotincLink } from '@/features/quotinc/quotinc.api';
import { ONBOARDING_ME_KEY, TOUR_EVENT, onboardingApi } from './onboarding.api';
import { buildTourSteps } from './tourSteps';
import { WELCOME_STEPS, type WelcomeStep } from './welcomeSteps';

// Lazy: almost every visit shows neither, so neither ships with the shell.
const WelcomeFlow = lazy(() => import('./WelcomeFlow').then((m) => ({ default: m.WelcomeFlow })));
const SpotlightTour = lazy(() => import('./SpotlightTour').then((m) => ({ default: m.SpotlightTour })));

interface Props {
  theme: 'light' | 'dark';
  onSetTheme: (t: 'light' | 'dark') => void;
}

/** The `/onboarding/me` read, shared by the shell host and the Hub home's checklist. */
export function useOnboardingMe() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ONBOARDING_ME_KEY,
    queryFn: ({ signal }) => onboardingApi.me(signal),
    enabled: !!user && user.role !== 'agent',
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Mounted once in the shell. Opens the welcome on an invited colleague's
 * first sign-in, runs the tour (after the welcome, from `?tour=1`, or from
 * the `amassa:tour` event that Preferences ▸ Help fires), reopens one
 * welcome step from `?welcome=<step>`, and sends a NEW workspace's admin to
 * the setup wizard once. Shows nothing at all while the API says
 * onboarding is not available (no migration) or for Asian Trails' existing
 * accounts.
 */
export function OnboardingHost({ theme, onSetTheme }: Props) {
  const { user, canSeeTile } = useAuth();
  const qc = useQueryClient();
  const me = useOnboardingMe();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { switcher, nav, hubTiles } = useModuleRegistries();
  const quotinc = useQuotincLink(!!user && user.role !== 'agent').on;

  const [welcome, setWelcome] = useState<WelcomeStep | null>(null);
  const [touring, setTouring] = useState(false);
  const autoWelcome = useRef(false);
  const autoSetup = useRef(false);

  // An invited colleague's first visit.
  useEffect(() => {
    if (me.data?.welcome?.required && !autoWelcome.current) {
      autoWelcome.current = true;
      setWelcome('you');
    }
  }, [me.data]);

  // Deep links from the checklist: ?welcome=<step>, ?tour=1.
  useEffect(() => {
    const w = params.get('welcome');
    const t = params.get('tour');
    if (!w && !t) return;
    if (w) setWelcome(WELCOME_STEPS.some((s) => s.id === w) ? (w as WelcomeStep) : 'you');
    else if (t) setTouring(true);
    const rest = new URLSearchParams(params);
    rest.delete('welcome');
    rest.delete('tour');
    setParams(rest, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    const on = () => {
      setWelcome(null);
      setTouring(true);
    };
    window.addEventListener(TOUR_EVENT, on);
    return () => window.removeEventListener(TOUR_EVENT, on);
  }, []);

  // A new workspace's admin lands in the wizard once (never Asian Trails:
  // its state is "legacy", never pending).
  useEffect(() => {
    const ws = me.data?.workspace;
    if (!ws || user?.role !== 'admin' || welcome || touring || autoSetup.current) return;
    if (ws.status === 'pending' && ws.autoOpen && !ws.startedAt && pathname === '/dashboard') {
      autoSetup.current = true;
      navigate('/admin/setup');
    }
  }, [me.data, user?.role, welcome, touring, pathname, navigate]);

  const stops = useMemo(() => {
    if (!touring) return [];
    const modules: ModuleDef[] = switcher.filter((m) =>
      m.appraisalsAware ? canSeeTile(m.tile) || canAccessAppraisals(user) : canSeeTile(m.tile),
    );
    const pages: Partial<Record<string, NavGroup[]>> = {};
    for (const m of modules) {
      pages[m.slug] = (nav[m.slug] ?? [])
        .map((g) => ({ ...g, items: g.items.filter((i) => isNavItemVisible(m.slug, i, user, canSeeTile)) }))
        .filter((g) => g.items.length > 0);
    }
    const hints: Record<string, string> = {};
    for (const t of hubTiles) if (t.mod) hints[t.mod] = t.hint;
    return buildTourSteps({
      modules,
      pages,
      hints,
      moduleTo: (m) => (m.appraisalsAware && !canSeeTile(m.tile) && m.nonAdminTo ? m.nonAdminTo : m.to),
      firstName: (user?.fullName || '').trim().split(/\s+/)[0] ?? '',
      isAdmin: user?.role === 'admin',
      hasChecklist: !!me.data?.checklist,
      quotinc,
    });
  }, [touring, switcher, nav, hubTiles, canSeeTile, user, me.data?.checklist, quotinc]);

  if (!user || user.role === 'agent') return null;

  return (
    <Suspense fallback={null}>
      {welcome && (
        <WelcomeFlow
          firstStep={welcome}
          theme={theme}
          onSetTheme={onSetTheme}
          onDone={(tour) => {
            setWelcome(null);
            if (tour) {
              if (pathname !== '/dashboard') navigate('/dashboard');
              setTouring(true);
            }
          }}
        />
      )}
      {touring && stops.length > 0 && (
        <SpotlightTour
          stops={stops}
          onClose={() => {
            setTouring(false);
            void onboardingApi
              .tourDone()
              .catch(() => undefined)
              .then(() => qc.invalidateQueries({ queryKey: ONBOARDING_ME_KEY }));
          }}
        />
      )}
    </Suspense>
  );
}
